<template>
  <div style="height: 100%" ref="mainContainer">
    <slot></slot>
    <div class="date-bar-container">
      <div class="d-flex">
        <v-btn-toggle v-model="dateOption" tile class="btn-toggle">
          <v-btn :value="dateOptions.custom">{{ $t('assets-date-option-custom') }}</v-btn>
        </v-btn-toggle>
        <!--
          NEX-9456: `mandatory` keeps one preset selected at all times. Without it,
          re-clicking the active preset deselected it (VItemGroup.updateSingle), leaving
          dateOption undefined — dynamicRange() then returned null and the auto-refresh
          silently stopped. Only this toggle is mandatory; the Custom toggle above must
          stay deselectable so Custom can be exited.
        -->
        <v-btn-toggle
          class="btn-toggle"
          v-model="dateOption"
          v-if="dateOption !== dateOptions.custom"
          tile
          mandatory
        >
          <v-btn :disabled="fetchingHistory" @click="onChangeDateOption(dateOptions.today)" :value="dateOptions.today">{{ $t('assets-date-option-today') }}</v-btn>
          <v-btn :disabled="fetchingHistory" @click="onChangeDateOption(dateOptions.yesterday)" :value="dateOptions.yesterday">{{ $t('assets-date-option-yesterday') }}</v-btn>
          <v-btn :disabled="fetchingHistory" @click="onChangeDateOption(dateOptions.thisWeek)" :value="dateOptions.thisWeek">{{ $t('assets-date-option-this-week') }}</v-btn>
          <v-btn :disabled="fetchingHistory" @click="onChangeDateOption(dateOptions.lastWeek)" :value="dateOptions.lastWeek">{{ $t('assets-date-option-last-week') }}</v-btn>
          <v-btn :disabled="fetchingHistory" @click="onChangeDateOption(dateOptions.lastSeen)" :value="dateOptions.lastSeen">{{ $t('last-seen') }}</v-btn>
        </v-btn-toggle>
        <v-btn v-if="dateOption !== dateOptions.custom" @click="onHistoryCancel" icon class="mt-1 ml-2 mr-1" color="primaryDark" small>
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="d-flex" v-if="dateOption === dateOptions.custom">
          <VueCtkDateTimePicker
            id="indoor-history-start-date-picker"
            class="date-control"
            v-model="historyStartDate"
            :label="$t('assets-history-start-date')"
            no-header
            no-button
          />
          <VueCtkDateTimePicker
            id="indoor-history-end-date-picker"
            class="date-control"
            v-model="historyEndDate"
            :label="$t('assets-history-end-date')"
            no-header
            no-button
            no-shortcuts
          />
          <v-btn
            class="date-bar-btn"
            color="primary"
            depressed
            @click="onHistorySubmit()"
            :disabled="invalidDates"
          >{{ $t('submit') }}</v-btn>
          <v-btn @click="onHistoryCancel" icon class="mt-1 ml-2 mr-1" color="primaryDark" small>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-row no-gutters align="center" justify="center">
        <v-col cols="12" sm="8">
          <div v-if="dateOption !== dateOptions.custom">
            <label class="date-display-label">
              {{ historyStartDateDisplayValue }} - {{ historyEndDateDisplayValue }}
            </label>
          </div>
        </v-col>
      </v-row>
      <div v-if="showHistoryNotAvailableMessage" class="no-events-message">
        {{ $t('assets-indoor-no-history-message') }}
      </div>
    </div>

    <!-- INDOOR EVENT LIST START -->
    <vue-draggable-resizable
      v-if="historyEventListData.length"
      :resizable="false"
      :parent="true"
      :z="1"
      :w="175"
      :h="'auto'"
      :max-height="400"
      :x="historyListXPos"
      :y="historyListYPos"
      :onDrag="onHistoryListDrag"
      class="event-list-wrapper"
    >
      <div class="event-list-container" ref="eventListContainer">
        <div class="active-floor-label" v-if="activeAreaId">
          <v-icon x-small>mdi-layers</v-icon>
          <span class="active-floor-name">{{ activeFloorName }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                class="freeze-area-btn"
                v-bind="attrs"
                v-on="on"
                @mousedown.stop
                @click.stop="toggleAreaFreeze"
              >
                <v-icon x-small :color="areaFrozen ? 'primary' : ''">
                  {{ areaFrozen ? 'mdi-lock' : 'mdi-lock-open-variant-outline' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ areaFrozen ? $t('assets-indoor-history-unfreeze-area') : $t('assets-indoor-history-freeze-area') }}</span>
          </v-tooltip>
        </div>
        <div v-if="largeEventSet" class="event-summary">
          Showing {{ historyEventListData.length.toLocaleString() }} indoor events. Map dots are grouped by position.
        </div>
        <ul class="event-list">
          <li
            class="event-list-item"
            v-for="(event, index) in historyEventListData"
            :ref="'indoorHistoryEventIndex' + index"
            :key="index"
            :class="{
              selected: index === currentHistoryEventIndex,
              'other-floor': activeAreaId && event.areaId && event.areaId !== activeAreaId,
              unresolved: !event.positionResolved,
            }"
            @click="onSelectHistoryEvent(index)"
          >
            <div class="d-flex align-center">
              <span><span class="dot-icon" :class="dotIconClass(event)"></span></span>
              <span>{{ fmtEventTime(event.eventTime) }}</span>
            </div>
            <v-divider></v-divider>
          </li>
        </ul>
      </div>
    </vue-draggable-resizable>
    <!-- INDOOR EVENT LIST END -->

  </div>
</template>

<script>
import { mapActions } from 'vuex';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import { scrollMixin } from '../../mixins/scrollParentToChild';
import { newLatLong } from '../../utils/calculateLatLng';
import mapboxgl from 'mapbox-gl';

const MAX_RANGE_DATE = 60;
const LARGE_EVENT_THRESHOLD = 500;
// NEX-9456: cadence for the Indoor History auto-refresh. 5 minutes matches the only
// existing data-refresh precedent in the app (assets-map-view autoRefreshInterval), but
// this is deliberately a separate local constant: the two timers are unrelated concerns
// and this one is meant to be trivial to tune pending Product/QA confirmation for AC1.
const HISTORY_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const SOURCE_LABELS = {
  EVENT_COORDINATES: 'Coordinates',
  CURRENT_BEACON_POSITION: 'Beacon coordinates',
  LOCATION_BEACON_MAP_POINT: 'Beacon position',
  UNRESOLVED: 'Map position unavailable',
};
const HISTORY_DOTS_SOURCE = 'indoor-history-dots-src-airfinder';
const HISTORY_DOTS_LAYER = 'indoor-history-dots-airfinder';

export default {
  components: {
    VueCtkDateTimePicker,
  },
  mixins: [scrollMixin],
  props: {
    tag: null,
    map: {
      default: null,
    },
    isAf3: {
      type: Boolean,
      default: false,
    },
    currentAreaId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      dateOption: null,
      fetchingHistory: false,
      historyEventListData: [],
      currentHistoryEventIndex: -1,
      showHistoryNotAvailableMessage: false,
      historyStartDate: this.$moment().startOf('day'),
      historyEndDate: this.$moment(),
      dateOptions: {
        custom: '1',
        today: '2',
        yesterday: '3',
        thisWeek: '4',
        lastWeek: '5',
        lastSeen: '6',
      },
      historyListXPos: 0,
      historyListYPos: 0,
      activeAreaId: this.currentAreaId,
      activeAreaName: '',
      // NEX-9354 (AC1): the displayed floor is resolved once, on the first history
      // response that yields an Area. Later date-range changes must never move it.
      initialAreaResolved: false,
      // NEX-9354 (AC1): only the most recent history request may apply its results.
      historyRequestId: 0,
      // NEX-9354 (AC2): while frozen, cross-floor history events stay selectable in
      // the list but never switch the displayed floorplan.
      areaFrozen: false,
      frozenAreaId: null,
      floorDataCache: {},
      historyDotFeatures: [],
      historyPopup: null,
      // NEX-9456: keeps a dynamic range's endDate advancing while the panel stays open.
      // Owned entirely by this component and cleared in beforeDestroy.
      historyRefreshTimer: null,
      // NEX-9456: left edge captured once when Last Seen is selected. tag.notSeenInDays
      // is a frozen snapshot, so its anchor must be frozen with it.
      lastSeenAnchorStart: null,
      // NEX-9456: identifies the selected event across a refetch.
      // currentHistoryEventIndex is positional and cannot survive one.
      selectedEventKey: null,
    };
  },
  mounted() {
    this.onChangeDateOption(this.dateOptions.lastSeen);
    this.dateOption = this.dateOptions.lastSeen;
    this.startHistoryRefreshTimer();
    this.setDefaultHistoryListPosition();
    window.addEventListener('resize', this.setDefaultHistoryListPosition);
  },
  computed: {
    historyStartDateDisplayValue() {
      let d = typeof this.historyStartDate === 'string'
        ? this.$moment(this.historyStartDate, 'YYYY-MM-DD hh:mm a')
        : this.historyStartDate;
      return d.format('M/D/YYYY hh:mm A');
    },
    historyEndDateDisplayValue() {
      let d = typeof this.historyEndDate === 'string'
        ? this.$moment(this.historyEndDate, 'YYYY-MM-DD hh:mm a')
        : this.historyEndDate;
      return d.format('M/D/YYYY hh:mm A');
    },
    invalidDates() {
      return (
        this.fetchingHistory ||
        this.historyStartDate === null ||
        this.historyEndDate === null ||
        new Date(this.historyEndDate) < new Date(this.historyStartDate)
      );
    },
    activeFloorName() {
      // NEX-9354 (AC1): the displayed floor may legitimately have no events in the
      // selected range, so prefer the name captured when the floor was established
      // rather than re-deriving it from the current list (which would show a raw id).
      if (this.activeAreaName) return this.activeAreaName;
      const event = this.historyEventListData.find(e => e.areaId === this.activeAreaId);
      return (event && event.areaName) ? event.areaName : (this.activeAreaId || '');
    },
    largeEventSet() {
      return this.historyEventListData.length > LARGE_EVENT_THRESHOLD;
    },
  },
  methods: {
    ...mapActions('tag', ['getTagIndoorHistory']),
    ...mapActions('area', ['getAF3Points']),
    sourceLabel(src) {
      return SOURCE_LABELS[src] || src || '';
    },
    fmtEventTime(t) {
      return this.$moment.utc(t).local().format('M/D/YYYY hh:mm A');
    },
    dotIconClass(event) {
      if (!event.positionResolved) return 'dot-unresolved';
      if (event.positionSource === 'LOCATION_BEACON_MAP_POINT') return 'dot-af2';
      return 'dot-af3';
    },
    // NEX-9456: `opts` is supplied only by the background refresh tick. A silent run
    // differs from a user submit in exactly three ways: it does not announce itself, it
    // does not tear the current view down before fetching, and it commits its candidate
    // date range only once a fresh response has actually arrived.
    async onHistorySubmit(opts) {
      const silent = !!(opts && opts.silent);
      const overrideStart = (opts && opts.startDate) || null;
      const overrideEnd = (opts && opts.endDate) || null;

      // NEX-9456: 'onSubmit' means "the user ran a new query" — a background tick is not
      // that. The parent handler is empty today, but the sibling map-history-bar binds
      // the same event name to a handler that clears map layers, so silent behaviour
      // must not depend on that staying true.
      if (!silent) {
        this.$emit('onSubmit');
        // NEX-9456: only a user submit tears down the current view up front. A silent
        // run keeps the last good history on screen until new data is available, so a
        // transient failure can never blank it.
        this.showHistoryNotAvailableMessage = false;
        this.historyEventListData = [];
        this.clearHistoryDots();
      }

      // NEX-9456: the candidate range stays local until the commit block below. Nothing
      // here may write this.historyStartDate / this.historyEndDate, or the displayed
      // range would advertise data that was never fetched.
      const rawStart = overrideStart || this.historyStartDate;
      const rawEnd = overrideEnd || this.historyEndDate;

      if (!rawStart || !rawEnd) return;

      const startDate = typeof rawStart === 'string'
        ? this.$moment(rawStart, 'YYYY-MM-DD hh:mm a')
        : rawStart;

      const endDate = typeof rawEnd === 'string'
        ? this.$moment(rawEnd, 'YYYY-MM-DD hh:mm a')
        : rawEnd;

      const endDateMax = startDate.clone().add(MAX_RANGE_DATE, 'days');
      if (endDate.diff(endDateMax) > 0) {
        // NEX-9456: for a silent run this is a complete no-op — no toast, no request, no
        // date commit, no data change. It also still precedes the historyRequestId
        // increment and fetchingHistory, preserving the NEX-9354 invariant that a
        // validation-only return can never strand the in-flight flag. A Last Seen panel
        // left open long enough to outgrow MAX_RANGE_DATE simply stops advancing; the
        // frozen left anchor is never slid or clamped to compensate.
        if (!silent) {
          this.$toasted.show(this.$t('assets-date-range-message'), {
            position: 'bottom-right',
            className: ['toast-error'],
            duration: 5000,
          });
        }
        return;
      }

      // NEX-9354 (AC1): claim this request only once we are committed to issuing it,
      // in the same step that raises fetchingHistory. Incrementing earlier would let a
      // validation-only return (no dates / range too long) invalidate an in-flight
      // request that then declines to clear the flag, disabling the bar permanently.
      // Every issued token therefore owns exactly one fetchingHistory lifecycle.
      const requestId = ++this.historyRequestId;
      this.fetchingHistory = true;
      // NEX-9456: a silent run keeps the current selection. It is restored against the
      // replacement list — or cleared — only once that list has actually committed, so a
      // failed refresh never drops the user's highlight.
      if (!silent) {
        this.currentHistoryEventIndex = -1;
        this.selectedEventKey = null;
      }

      try {
        const payload = {
          id: this.tag.id,
          startDate: startDate.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endDate: endDate.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          // NEX-9456: an unattended refresh must not raise the app-wide blocking
          // overlay that the shared axios loading interceptor would otherwise show.
          hideLoadingIndicator: silent,
        };
        const response = await this.getTagIndoorHistory(payload);
        // NEX-9354 (AC1): a newer request has been issued — drop this response.
        if (requestId !== this.historyRequestId) return;

        // NEX-9456: commit. Reached only for a fresh, non-superseded success, so the
        // displayed range always describes the data currently on screen. A superseded
        // silent response cannot stamp its endDate onto a range the user has since
        // changed.
        if (overrideStart) this.historyStartDate = overrideStart;
        if (overrideEnd) this.historyEndDate = overrideEnd;

        const events = (response && response.events) ? response.events : [];
        this.historyEventListData = events;
        this.showHistoryNotAvailableMessage = events.length === 0;
        this.fetchingHistory = false;

        if (events.length === 0) {
          // NEX-9456: a successful empty response is valid new information — Today just
          // after local midnight is the normal case — not a failure. A silent run did
          // not pre-clear, so the superseded dots and selection have to go here.
          this.clearHistoryDots();
          this.currentHistoryEventIndex = -1;
          this.selectedEventKey = null;
          return;
        }

        // NEX-9354 (AC1): once a floor has been established, a date-range change
        // keeps it — even when the new range has no events on that floor. Only an
        // explicit cross-floor event click may move the displayed Area from here on.
        if (this.initialAreaResolved) {
          await this.drawHistoryDots();
          // NEX-9456: drawHistoryDots rebuilds the layer with default paint, so the
          // highlight can only be re-applied after it.
          this.restoreSelectedEvent();
          return;
        }

        const initialAreaId = this.determineInitialArea(events);
        if (!initialAreaId) return;

        this.initialAreaResolved = true;

        if (initialAreaId !== this.activeAreaId) {
          this.$emit('switch-area', initialAreaId);
        } else {
          this.setActiveAreaName(initialAreaId);
          await this.drawHistoryDots();
        }
      } catch (error) {
        if (requestId !== this.historyRequestId) return;
        // NEX-9456: a silent failure leaves the last good view completely untouched —
        // list, dots, selected event and both displayed dates. fetchingHistory is still
        // released so the next tick is not wedged.
        if (!silent) this.showHistoryNotAvailableMessage = true;
        this.fetchingHistory = false;
      }
    },
    // NEX-9456: single source of truth for which presets have a moving range and how it
    // is computed, shared by the preset buttons and the refresh tick so the two can
    // never disagree. Returns null for every static range — Yesterday, Last Week,
    // Custom, an unset option, and the pinned branch of Last Seen.
    dynamicRange(option) {
      const week = 7;
      switch (option) {
        case this.dateOptions.today:
          // Both edges are recomputed: past local midnight "Today" must become the new
          // day, so a frozen left edge would leave yesterday inside a range still
          // labelled Today.
          return {
            startDate: this.$moment().startOf('day'),
            endDate: this.$moment(),
          };
        case this.dateOptions.thisWeek:
          // Recomputed in full for the same reason, across the week boundary.
          return {
            startDate: this.$moment().startOf('week').add(1, 'day'),
            endDate: this.$moment(),
          };
        case this.dateOptions.lastSeen:
          if (!this.tag || this.tag.notSeenInDays >= MAX_RANGE_DATE - week * 2) return null;
          if (!this.lastSeenAnchorStart) return null;
          // Unlike Today and This Week, the left edge is an anchor rather than a
          // function of now: tag.notSeenInDays is a snapshot taken when the tag was
          // normalized and is never refreshed while this panel is open, so re-deriving
          // the start from it on every tick would slide the window forward against a
          // stale value and eventually walk past the sighting the preset is named after.
          return {
            startDate: this.lastSeenAnchorStart.clone(),
            endDate: this.$moment(),
          };
        default:
          return null;
      }
    },
    // NEX-9456: mirrors the startAutoRefreshTimer/clearAutoRefreshTimer idiom in
    // assets-map-view, but deliberately owns its own interval.
    startHistoryRefreshTimer() {
      this.clearHistoryRefreshTimer();
      this.historyRefreshTimer = setInterval(
        this.onHistoryRefreshTick,
        HISTORY_REFRESH_INTERVAL_MS
      );
    },
    clearHistoryRefreshTimer() {
      if (this.historyRefreshTimer) {
        clearInterval(this.historyRefreshTimer);
        this.historyRefreshTimer = null;
      }
    },
    // NEX-9456: the fix itself. A dynamic range keeps advancing while the panel is open;
    // a static one never issues a background request. There is deliberately no "user is
    // busy" pause condition — any such guard could suspend refresh indefinitely, which
    // is the defect this ticket exists to remove.
    onHistoryRefreshTick() {
      // Reuse the NEX-9354 in-flight flag rather than adding a second request-state
      // system, so a request slower than the cadence cannot let ticks stack.
      if (this.fetchingHistory) return;
      const range = this.dynamicRange(this.dateOption);
      if (!range) return;
      this.onHistorySubmit({
        silent: true,
        startDate: range.startDate,
        endDate: range.endDate,
      });
    },
    // NEX-9456: stable identity for a history event across a refetch. The endpoint
    // returns raw rows with no id — getIndoorHistory does not normalize — and eventTime
    // alone is second-resolution, so it is not provably unique for a tag reporting every
    // few seconds. This mirrors the position keys drawHistoryDots already builds. Rows
    // that still collide are indistinguishable in every rendered form (same list label,
    // same grouped dot, same popup), so matching the first is always visually correct.
    eventKey(event) {
      if (!event) return null;
      const position = event.positionSource === 'LOCATION_BEACON_MAP_POINT'
        ? `MP|${event.mapPointX}|${event.mapPointY}`
        : `XY|${event.x}|${event.y}`;
      return `${event.eventTime}|${event.areaId}|${event.positionSource}|${position}`;
    },
    // NEX-9456: re-apply the highlight after a refresh replaced the list. Deliberately
    // not onSelectHistoryEvent(): a background tick must not scroll the list and must
    // not be able to emit switch-area.
    restoreSelectedEvent() {
      if (!this.selectedEventKey) return;
      const index = this.historyEventListData.findIndex(
        e => this.eventKey(e) === this.selectedEventKey
      );
      if (index === -1) {
        this.currentHistoryEventIndex = -1;
        this.selectedEventKey = null;
        return;
      }
      this.currentHistoryEventIndex = index;
      this.applyDotHighlight(this.historyEventListData[index]);
    },
    // NEX-9456: extracted verbatim from onSelectHistoryEvent so the highlight can also
    // be re-applied after drawHistoryDots rebuilds the layer with default paint. No
    // behaviour change on the user-driven path.
    applyDotHighlight(event) {
      const isMapPoint = event.positionSource === 'LOCATION_BEACON_MAP_POINT';
      const isDrawable = event.positionResolved && (
        (isMapPoint && typeof event.mapPointX === 'number' && typeof event.mapPointY === 'number') ||
        (!isMapPoint && event.x != null && event.y != null)
      );

      if (isDrawable && this.map && this.map.getLayer(HISTORY_DOTS_LAYER)) {
        const matchExpr = isMapPoint
          ? ['all', ['==', ['get', 'isMapPoint'], true], ['==', ['get', 'mpx'], event.mapPointX], ['==', ['get', 'mpy'], event.mapPointY]]
          : ['all', ['==', ['get', 'isMapPoint'], false], ['==', ['get', 'x'], event.x], ['==', ['get', 'y'], event.y]];
        this.map.setPaintProperty(HISTORY_DOTS_LAYER, 'circle-color', [
          'case', matchExpr, '#ff6b35',
          ['==', ['get', 'isMapPoint'], true], '#ffa726', '#faff67',
        ]);
        this.map.setPaintProperty(HISTORY_DOTS_LAYER, 'circle-radius', [
          'case', matchExpr, 12, 9,
        ]);
      }
    },
    // NEX-9354 (AC1): the history endpoint sends no sort parameter, so array order
    // carries no guarantee. Pick the Area of the most recent event instead, using the
    // same UTC parser as the list display so the two can never disagree.
    determineInitialArea(events) {
      const isDrawable = (e) => {
        if (!e.areaId || !e.positionResolved) return false;
        if (e.positionSource === 'LOCATION_BEACON_MAP_POINT') {
          return e.mapPointX != null && e.mapPointY != null;
        }
        return e.x != null && e.y != null;
      };
      const latestAreaId = (candidates) => {
        let bestAreaId = null;
        let bestTime = null;
        candidates.forEach(e => {
          const time = this.$moment.utc(e.eventTime).valueOf();
          if (isNaN(time)) return;
          if (bestTime === null || time > bestTime) {
            bestTime = time;
            bestAreaId = e.areaId;
          }
        });
        // No parseable timestamp in this set: fall back to the first candidate so a
        // floor is still resolved, matching the previous behaviour.
        if (!bestAreaId && candidates.length) return candidates[0].areaId;
        return bestAreaId;
      };

      const fromDrawable = latestAreaId(events.filter(isDrawable));
      if (fromDrawable) return fromDrawable;
      return latestAreaId(events.filter(e => e.areaId));
    },
    setActiveAreaName(areaId) {
      if (!areaId) {
        this.activeAreaName = '';
        return;
      }
      const event = this.historyEventListData.find(e => e.areaId === areaId && e.areaName);
      // Keep the last known name when the current range has no events for this floor.
      if (event) this.activeAreaName = event.areaName;
    },
    // NEX-9354 (AC2): freeze pins the displayed floor. It is local UI state only — it
    // never touches Vuex currentArea, the Mapbox instance, or map interactivity.
    toggleAreaFreeze() {
      if (this.areaFrozen) {
        this.areaFrozen = false;
        this.frozenAreaId = null;
        return;
      }
      this.areaFrozen = true;
      this.frozenAreaId = this.activeAreaId;
    },
    async onAreaSwitched(areaId) {
      // NEX-9354 (AC1): a name cached for the previous Area must never survive a real
      // floor change. Clear it first so an Area with no usable areaName in the list
      // falls back safely instead of showing the previous floor's name. The cache is
      // only preserved when the Area is unchanged (same floor, new date range).
      if (areaId !== this.activeAreaId) {
        this.activeAreaName = '';
      }
      this.activeAreaId = areaId;
      this.setActiveAreaName(areaId);
      await this.drawHistoryDots();
    },
    async drawHistoryDots() {
      // NEX-9354 (AC1): pin the Area for the whole operation so an Area change while
      // awaiting AF3 reference data cannot draw this floor's dots on the next floor.
      const areaId = this.activeAreaId;
      if (!this.map || !areaId || !this.historyEventListData.length) return;

      this.clearHistoryDots();

      const features = [];

      // === AF3 path: x/y → newLatLong ===
      const af3Events = this.historyEventListData.filter(e =>
        e.areaId === areaId &&
        e.positionResolved &&
        e.positionSource !== 'LOCATION_BEACON_MAP_POINT' &&
        e.x != null && e.y != null
      );

      if (af3Events.length) {
        const floorData = await this.getOrFetchFloorData(areaId);
        // The displayed floor changed while awaiting — abandon this draw entirely.
        if (this.activeAreaId !== areaId) return;
        if (floorData && floorData.distance && floorData.mapDistance) {
          const scale = (floorData.mapDistance * 1000) / floorData.distance;
          const referencePoint = [floorData.originPointLat, floorData.originPointLng];
          const posMap = new Map();
          af3Events.forEach(e => {
            const key = `${e.x}|${e.y}`;
            if (!posMap.has(key)) {
              posMap.set(key, {
                x: e.x, y: e.y, events: [],
                areaName: e.areaName || '', zoneName: e.zoneName || '',
                locationType: e.locationType || '', positionSource: e.positionSource || '',
              });
            }
            posMap.get(key).events.push(e);
          });
          posMap.forEach(group => {
            const coords = newLatLong(group.x / 10, group.y / 10, 0, scale, referencePoint);
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: coords },
              properties: {
                count: group.events.length,
                firstTime: group.events[0].eventTime,
                lastTime: group.events[group.events.length - 1].eventTime,
                areaName: group.areaName, zoneName: group.zoneName,
                locationType: group.locationType, positionSource: group.positionSource,
                isMapPoint: false,
                x: group.x, y: group.y,
                mpx: null, mpy: null,
                locationBeaconName: '', locationMac: '', mapPoint: '',
              },
            });
          });
        }
      }

      // === AF2 mapPoint path: use [mapPointX, mapPointY] directly as GeoJSON coordinates ===
      // This mirrors assets-map-view.vue drawDevices() which does:
      //   coordinates: tag.locationMapPoint  (= mapPoint.split(',').map(Number) = [mapPointX, mapPointY])
      const mapPointEvents = this.historyEventListData.filter(e =>
        e.areaId === areaId &&
        e.positionResolved &&
        e.positionSource === 'LOCATION_BEACON_MAP_POINT' &&
        typeof e.mapPointX === 'number' && typeof e.mapPointY === 'number'
      );

      if (mapPointEvents.length) {
        const posMap = new Map();
        mapPointEvents.forEach(e => {
          const key = `MP|${e.mapPointX}|${e.mapPointY}`;
          if (!posMap.has(key)) {
            posMap.set(key, {
              mapPointX: e.mapPointX, mapPointY: e.mapPointY, events: [],
              areaName: e.areaName || '', zoneName: e.zoneName || '',
              locationType: e.locationType || '', positionSource: e.positionSource || '',
              locationBeaconName: e.locationBeaconName || '',
              locationMac: e.locationMac || '', mapPoint: e.mapPoint || '',
            });
          }
          posMap.get(key).events.push(e);
        });
        posMap.forEach(group => {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [group.mapPointX, group.mapPointY] },
            properties: {
              count: group.events.length,
              firstTime: group.events[0].eventTime,
              lastTime: group.events[group.events.length - 1].eventTime,
              areaName: group.areaName, zoneName: group.zoneName,
              locationType: group.locationType, positionSource: group.positionSource,
              isMapPoint: true,
              x: null, y: null,
              mpx: group.mapPointX, mpy: group.mapPointY,
              locationBeaconName: group.locationBeaconName,
              locationMac: group.locationMac, mapPoint: group.mapPoint,
            },
          });
        });
      }

      if (!features.length) return;

      this.historyDotFeatures = features;
      const geojsonData = { type: 'FeatureCollection', features };

      if (this.map.getSource(HISTORY_DOTS_SOURCE)) {
        this.map.getSource(HISTORY_DOTS_SOURCE).setData(geojsonData);
      } else {
        this.map.addSource(HISTORY_DOTS_SOURCE, { type: 'geojson', data: geojsonData });

        this.map.addLayer({
          id: HISTORY_DOTS_LAYER,
          source: HISTORY_DOTS_SOURCE,
          type: 'circle',
          paint: {
            'circle-radius': 9,
            'circle-color': ['case', ['==', ['get', 'isMapPoint'], true], '#ffa726', '#faff67'],
            'circle-opacity': 0.9,
            'circle-stroke-width': ['case', ['>', ['get', 'count'], 1], 2.5, 1.5],
            'circle-stroke-color': [
              'case',
              ['all', ['==', ['get', 'isMapPoint'], true], ['>', ['get', 'count'], 1]], '#bf360c',
              ['==', ['get', 'isMapPoint'], true], '#e65100',
              ['>', ['get', 'count'], 1], '#1565c0',
              '#555'
            ],
          },
        });

        this.map.on('mouseenter', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
        this.map.on('click', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
        this.map.on('mouseleave', HISTORY_DOTS_LAYER, this.onDotMouseLeave);
      }
    },
    buildDotPopupHtml(props) {
      const isMapPoint = props.isMapPoint === true || props.isMapPoint === 'true';
      const header = isMapPoint ? 'Historical beacon position' : 'Historical coordinate position';
      const areaZone = [props.areaName, props.zoneName].filter(Boolean).join(' / ') || '--';
      const fmt = t => this.$moment.utc(t).local().format('M/D/YYYY hh:mm A');
      const timeBlock = props.count > 1
        ? `<b>${props.count} events</b><br><b>First:</b> ${fmt(props.firstTime)}<br><b>Last:</b> ${fmt(props.lastTime)}`
        : `<b>Time:</b> ${fmt(props.firstTime)}`;
      const beaconRow = props.locationBeaconName ? `<br><b>Beacon:</b> ${props.locationBeaconName}` : '';
      const typeRow = props.locationType ? `<br><b>Type:</b> ${props.locationType}` : '';
      return `<div style="font-size:12px;line-height:1.7"><b>${header}</b><br>${timeBlock}<br><b>Area:</b> ${areaZone}${beaconRow}${typeRow}<br><b>Source:</b> ${this.sourceLabel(props.positionSource)}</div>`;
    },
    onDotMouseEnter(e) {
      if (!e.features || !e.features.length) return;
      this.map.getCanvas().style.cursor = 'pointer';
      const html = this.buildDotPopupHtml(e.features[0].properties);
      if (this.historyPopup) this.historyPopup.remove();
      this.historyPopup = new mapboxgl.Popup({ closeOnClick: true, maxWidth: '240px' })
        .setLngLat(e.features[0].geometry.coordinates.slice())
        .setHTML(html)
        .addTo(this.map);
    },
    onDotMouseLeave() {
      if (this.map) this.map.getCanvas().style.cursor = '';
    },
    clearHistoryDots() {
      if (!this.map) return;
      if (this.historyPopup) {
        this.historyPopup.remove();
        this.historyPopup = null;
      }
      this.historyDotFeatures = [];
      this.map.off('mouseenter', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
      this.map.off('click', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
      this.map.off('mouseleave', HISTORY_DOTS_LAYER, this.onDotMouseLeave);
      if (this.map.getLayer(HISTORY_DOTS_LAYER)) {
        this.map.removeLayer(HISTORY_DOTS_LAYER);
      }
      if (this.map.getSource(HISTORY_DOTS_SOURCE)) {
        this.map.removeSource(HISTORY_DOTS_SOURCE);
      }
    },
    async getOrFetchFloorData(areaId) {
      if (this.floorDataCache[areaId]) return this.floorDataCache[areaId];
      try {
        const data = await this.getAF3Points(areaId);
        if (data) {
          this.$set(this.floorDataCache, areaId, data);
          return data;
        }
      } catch (e) {
        // area has no AF3 reference points — dots not drawable
      }
      return null;
    },
    onHistoryCancel() {
      this.clearHistoryDots();
      this.$emit('onCancel');
    },
    async onSelectHistoryEvent(index) {
      this.currentHistoryEventIndex = index;
      if (this.$refs['indoorHistoryEventIndex' + index]) {
        scrollMixin.methods.scrollParentToChild(
          this.$refs.eventListContainer,
          this.$refs['indoorHistoryEventIndex' + index][0]
        );
      }
      const event = this.historyEventListData[index];
      if (!event) return;

      // NEX-9456: remember what the user picked so a background refresh can find it
      // again in the replacement list.
      this.selectedEventKey = this.eventKey(event);

      if (event.areaId && event.areaId !== this.activeAreaId) {
        // NEX-9354 (AC2): frozen — keep the list selection and the displayed floor.
        // The event is on another floorplan, so there is no dot here to highlight.
        if (this.areaFrozen) return;
        this.$emit('switch-area', event.areaId);
        return;
      }

      this.applyDotHighlight(event);
    },
    onChangeDateOption(option) {
      if (option === this.dateOption) return;
      switch (option) {
        case this.dateOptions.today: {
          // NEX-9456: same helper the refresh tick uses, so the button and the timer
          // can never compute the range differently.
          const range = this.dynamicRange(option);
          this.historyStartDate = range.startDate;
          this.historyEndDate = range.endDate;
          break;
        }
        case this.dateOptions.yesterday:
          this.historyStartDate = this.$moment().subtract(1, 'day').startOf('day');
          this.historyEndDate = this.$moment().subtract(1, 'day').endOf('day');
          break;
        case this.dateOptions.thisWeek: {
          const range = this.dynamicRange(option);
          this.historyStartDate = range.startDate;
          this.historyEndDate = range.endDate;
          break;
        }
        case this.dateOptions.lastWeek:
          this.historyStartDate = this.$moment().subtract(1, 'week').startOf('week').add(1, 'day');
          this.historyEndDate = this.$moment().subtract(1, 'week').endOf('week').add(1, 'day');
          break;
        case this.dateOptions.lastSeen: {
          const week = 7;
          // NEX-9456: capture the anchor once, here. dynamicRange() reuses it on every
          // tick instead of re-deriving it from the frozen tag.notSeenInDays.
          this.lastSeenAnchorStart = this.$moment()
            .subtract(this.tag.notSeenInDays + week * 2, 'day')
            .startOf('day');
          this.historyStartDate = this.lastSeenAnchorStart.clone();
          if (this.tag.notSeenInDays < MAX_RANGE_DATE - week * 2) {
            this.historyEndDate = this.$moment();
          } else {
            this.historyEndDate = this.$moment().subtract(this.tag.notSeenInDays, 'day').endOf('day');
          }
          break;
        }
        default:
          break;
      }
      this.onHistorySubmit();
    },
    setDefaultHistoryListPosition() {
      const mainWidth = this.$refs.mainContainer.clientWidth;
      const mainHeight = this.$refs.mainContainer.clientHeight;
      this.historyListXPos = Math.floor(mainWidth - mainWidth * 0.02 - 280);
      this.historyListYPos = Math.floor(mainHeight * 0.2);
    },
    onHistoryListDrag(x, y) {
      this.historyListXPos = x;
      this.historyListYPos = y;
    },
  },
  beforeDestroy() {
    // NEX-9456: the component is v-if'd on showIndoorHistoryBar, so closing the panel,
    // selecting another tag, switching to outdoor history, changing area and leaving the
    // view all land here. Clearing the interval is the only cleanup needed — no parent
    // bookkeeping.
    this.clearHistoryRefreshTimer();
    this.clearHistoryDots();
    window.removeEventListener('resize', this.setDefaultHistoryListPosition);
  },
};
</script>

<style lang="scss" scoped>
.date-bar-container {
  position: absolute;
  right: 0;
  z-index: 3;
  margin: 5px 10px 10px 120px;
  border: 1px solid white;
  background-color: white;
  border-radius: 3px;

  .btn-toggle {
    margin: 5px;
  }
}

.date-control {
  padding: 5px;
  width: 220px;

  @media (min-width: 1280px) {
    width: 250px;
  }
}

::v-deep #indoor-history-start-date-picker-input,
::v-deep #indoor-history-end-date-picker-input {
  font-size: 12px;

  @media (min-width: 1280px) {
    font-size: 14px;
  }
}

.date-bar-btn {
  align-self: center;
  margin-right: 5px;
}

.date-display-label {
  margin: 10px 10px 10px 100px;
}

.event-list-wrapper {
  position: absolute;
}

.event-list-container {
  background-color: white;
  opacity: 0.8;
  padding: 10px 2px 10px 5px;
  z-index: 1;
  border: 1px solid;
  border-radius: 3px;
  text-align: left;
  overflow-y: auto;
  width: 175px;
  max-height: 350px;
  cursor: move;

  .event-summary {
    padding: 4px 8px;
    font-size: 10px;
    color: #555;
    background: #fff8e1;
    border-bottom: 1px solid #ffe082;
  }

  .event-list {
    list-style: none;
    padding: 0;

    .selected {
      background: radial-gradient(#faff67, transparent);
    }

    .unresolved {
      opacity: 0.6;
    }

    .other-floor {
      opacity: 0.42;
      background-color: #f5f5f5;
    }

    .event-list-item {
      padding: 5px;
      cursor: pointer;
      font-size: 11px;

      &:hover {
        background: radial-gradient(#76e9ff, transparent);
      }

      .dot-icon {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 0 4px 0 2px;
        flex-shrink: 0;

        &.dot-af2 {
          background-color: #ffa726;
          border: 1.5px solid #e65100;
        }

        &.dot-af3 {
          background-color: #faff67;
          border: 1.5px solid #555;
        }

        &.dot-unresolved {
          background-color: #ccc;
          border: 1.5px solid #999;
        }
      }
    }
  }
}

.active-floor-label {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  background: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
  display: flex;
  align-items: center;
  gap: 4px;

  .active-floor-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .freeze-area-btn {
    flex: 0 0 auto;
    cursor: pointer;
  }
}

.no-events-message {
  padding: 8px 10px 6px;
  font-size: 12px;
  color: #555;
  border-top: 1px solid #e0e0e0;
}
</style>
